import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Camera, Check, ChevronLeft, ImagePlus } from 'lucide-react';
import { api, ApiError, photoUrl, type ProfilePhoto } from '../api/client.ts';
import { useAuth } from '../auth/AuthContext.tsx';
import { Avatar } from '../components/Avatar.tsx';
import { CameraScreen } from '../components/CameraScreen.tsx';
import './SettingsScreen.css';
import './ProfileScreen.css';

/**
 * «Профиль» — the name everyone else reads, and the photographs one of which is
 * the avatar beside it.
 *
 * The nickname is shown and cannot be edited, which is the point of showing it:
 * it is what somebody types into «Начать чат» to reach you, so it has to stay
 * findable once a display name has covered it everywhere else.
 *
 * Photos can only be added. A tap on one makes it the avatar — there is nothing
 * else a tile does, which is why the section says so rather than leaving people
 * to discover it.
 */
export function ProfileScreen() {
  const navigate = useNavigate();
  const { user, applyIdentity } = useAuth();

  const [name, setName] = useState(user?.displayName ?? '');
  const [photos, setPhotos] = useState<ProfilePhoto[]>([]);
  const [avatarId, setAvatarId] = useState(user?.avatarPhotoId ?? null);
  const [busy, setBusy] = useState(false);
  /** True while bytes are going up, which the grid draws as a leading tile. */
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cameraOpen, setCameraOpen] = useState(false);

  const fileInput = useRef<HTMLInputElement>(null);

  useEffect(() => {
    api
      .profilePhotos()
      .then(setPhotos)
      // Offline: the name still saves when the connection comes back, and an
      // empty grid says as much as an error would.
      .catch(() => {});
  }, []);

  const trimmed = name.trim();
  const changed = trimmed !== (user?.displayName ?? '');

  async function save() {
    setBusy(true);
    setError(null);
    try {
      applyIdentity(await api.saveProfile(trimmed));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Не удалось сохранить имя');
    }
    setBusy(false);
  }

  async function addPhoto(file: File) {
    setUploading(true);
    setError(null);
    try {
      const added = await api.addProfilePhoto(file);
      setPhotos((current) => [added.photo, ...current]);
      // The first photo becomes the avatar on the server, so this is where the
      // tile that arrives already wearing the badge gets it.
      setAvatarId(added.avatarPhotoId);
      if (user) applyIdentity({ ...user, avatarPhotoId: added.avatarPhotoId });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Не удалось загрузить фото');
    }
    setUploading(false);
  }

  async function chooseAvatar(photoId: string) {
    if (photoId === avatarId) return;
    const previous = avatarId;
    // Optimistic: the badge moves under the finger, and comes back if the
    // server disagrees — the same shape as the notification toggle.
    setAvatarId(photoId);
    if (user) applyIdentity({ ...user, avatarPhotoId: photoId });
    try {
      await api.setAvatar(photoId);
    } catch {
      setAvatarId(previous);
      if (user) applyIdentity({ ...user, avatarPhotoId: previous });
      setError('Не удалось поставить фото на аватар');
    }
  }

  if (cameraOpen) {
    return (
      <CameraScreen
        maxUploadBytes={user?.maxUploadBytes ?? 0}
        photoOnly
        onCapture={(file) => {
          setCameraOpen(false);
          void addPhoto(file);
        }}
        onGallery={() => {
          setCameraOpen(false);
          fileInput.current?.click();
        }}
        onSystemCamera={null}
        onClose={() => setCameraOpen(false)}
      />
    );
  }

  return (
    <div className="app-frame settings-screen">
      <header className="settings-header settings-header-divided">
        <button
          type="button"
          className="icon-button settings-back"
          onClick={() => navigate('/settings')}
          aria-label="Назад"
        >
          <ChevronLeft size={22} />
        </button>
        <h2 className="settings-heading">Профиль</h2>
      </header>

      <div className="settings-list profile-list">
        <div className="profile-head">
          <Avatar className="profile-avatar" photoId={avatarId} name={user?.displayName} />
          <div className="profile-nickname">@{user?.username}</div>
          <div className="profile-nickname-note">
            Никнейм не меняется — по нему вас находят в «Начать чат»
          </div>
        </div>

        <div className="profile-section">
          <label className="field-label" htmlFor="profile-name">
            Отображаемое имя
          </label>
          <input
            id="profile-name"
            className="text-field"
            value={name}
            onChange={(event) => setName(event.target.value)}
            placeholder={user?.username}
            maxLength={48}
          />
          <div className="profile-hint">Его видят все — в списке чатов, в группах и в звонках</div>
          <button
            type="button"
            className="primary-button profile-save"
            onClick={() => void save()}
            disabled={busy || !changed}
          >
            Сохранить
          </button>
          {error && <div className="form-error">{error}</div>}
        </div>

        <div className="profile-section">
          <div className="field-label">Фото</div>
          <div className="profile-hint">Нажмите на фото, чтобы поставить его на аватар</div>

          <div className="profile-grid">
            {uploading && <div className="profile-tile profile-tile-pending" />}
            {photos.map((photo) => (
              <button
                key={photo.id}
                type="button"
                className={`profile-tile${photo.id === avatarId ? ' profile-tile-current' : ''}`}
                onClick={() => void chooseAvatar(photo.id)}
                aria-pressed={photo.id === avatarId}
                aria-label={photo.id === avatarId ? 'Это фото на аватаре' : 'Поставить на аватар'}
              >
                <img src={photoUrl(photo.id, { variant: 'thumb' })} alt="" loading="lazy" />
                {photo.id === avatarId && (
                  <span className="profile-tile-badge">
                    <Check size={13} strokeWidth={3} />
                  </span>
                )}
              </button>
            ))}
          </div>

          <div className="profile-actions">
            <button
              type="button"
              className="profile-add"
              onClick={() => fileInput.current?.click()}
              disabled={uploading}
            >
              <ImagePlus size={18} />
              Из галереи
            </button>
            <button
              type="button"
              className="profile-add"
              onClick={() => setCameraOpen(true)}
              disabled={uploading}
            >
              <Camera size={18} />
              Снять фото
            </button>
          </div>

          <input
            ref={fileInput}
            className="profile-file"
            type="file"
            accept="image/*"
            onChange={(event) => {
              const file = event.target.files?.[0];
              // Cleared so picking the same file twice still fires `change`.
              event.target.value = '';
              if (file) void addPhoto(file);
            }}
          />
        </div>
      </div>
    </div>
  );
}
